import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { workspace } from "../lib/workspace";
import type { PreviewGroup, PreviewPolicy } from "../lib/workspace";
import { useApi } from "../lib/useApi";
import { relativeTime, shortSha } from "../lib/format";
import { Button, CardSkeleton } from "../components/ui";
import StatusBadge from "../components/StatusBadge";

export function useAction(reload: () => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  async function run(action: () => Promise<unknown>, success = "Saved") {
    if (busy) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await action();
      setMessage(success);
      reload();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "The action failed. Please retry.",
      );
    } finally {
      setBusy(false);
    }
  }
  return {
    busy,
    run,
    feedback: (
      <>
        {error && (
          <p className="alert error" role="alert">
            {error}
          </p>
        )}
        {message && (
          <p className="platform-notice" role="status">
            {message}
          </p>
        )}
      </>
    ),
  };
}

export default function ProjectTools() {
  const { projectId = "" } = useParams();
  const { data, error } = useApi(
    () => api.project(projectId),
    [projectId],
    15_000,
  );
  const [params, setParams] = useSearchParams();
  const requestedTab = params.get("tab") ?? "command";
  const tab = ["command", "previews", "variables", "team", "activity"].includes(requestedTab) ? requestedTab : "command";
  const setTab = (value: string) => setParams(current => { current.set("tab", value); return current });
  if (error) return <p className="alert error">{error}</p>;
  if (!data) return <CardSkeleton />;
  const role = data.role ?? "owner";
  return (
    <>
      <Link to={`/projects/${projectId}`} className="tiny muted">
        ← {data.name}
      </Link>
      <header className="page-head">
        <div>
          <p className="platform-eyebrow">APPLICATION WORKSPACE</p>
          <h1>{data.name}</h1>
          <p className="subtitle">
            Release evidence, preview environments, and your team.
          </p>
        </div>
        <span className="platform-role">{role}</span>
      </header>
      <nav className="advanced-tabs" aria-label="Workspace sections">
        {[
          ["command", "Overview"],
          ["previews", "Previews"],
          ...(role !== "viewer" ? [["variables", "Environment"]] : []),
          ["team", "Team"],
          ["activity", "Activity"],
        ].map(([key, label]) => (
          <button
            key={key}
            className={`btn ${tab === key ? "primary" : "ghost"}`}
            aria-pressed={tab === key}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </nav>
      {tab === "command" && <Command projectId={projectId} />}
      {tab === "previews" && <Previews projectId={projectId} role={role} />}
      {tab === "variables" && role !== "viewer" && (
        <Variables projectId={projectId} services={data.services} />
      )}
      {tab === "team" && (
        <Team projectId={projectId} owner={role === "owner"} />
      )}
      {tab === "activity" && <Activity projectId={projectId} />}
    </>
  );
}

function Command({ projectId }: { projectId: string }) {
  const { data, error, reload } = useApi(
    () => workspace.command(projectId),
    [projectId],
    10_000,
  );
  if (error) return <p className="alert error">{error}</p>;
  if (!data) return <CardSkeleton />;
  return (
    <div className="stack">
      <div className="row">
        <h2>Release evidence</h2>
        <span className="grow" />
        <span className="tiny muted">
          Updated {relativeTime(data.observed_at)}
        </span>
        <Button onClick={reload}>Refresh</Button>
      </div>
      <div className="platform-grid">
        {data.issues.map((issue) => (
          <article className="card" key={issue.deployment_id}>
            <p className="platform-role">Needs attention</p>
            <h3>{issue.category}</h3>
            <p className="platform-failure">
              {issue.error || "No error details recorded"}
            </p>
            <p>{issue.action}</p>
            <p className="tiny muted">{issue.basis}</p>
            <Link to={`/projects/${projectId}`}>
              Review release and recovery controls →
            </Link>
          </article>
        ))}
      </div>
      {data.issues.length === 0 && (
        <p className="platform-notice">
          No failed components in the recent release history.
        </p>
      )}
      <section className="card">
        <h2>Database dependencies</h2>
        {!data.dependencies.length && (
          <p className="muted">No linked databases.</p>
        )}
        {data.dependencies.map((d) => (
          <div className="workspace-dependency" key={d.id}>
            <div>
              <strong>{d.name}</strong>
              {d.error && <p className="platform-failure">{d.error}</p>}
            </div>
            <StatusBadge status={d.status} />
          </div>
        ))}
      </section>
      <section className="card">
        <h2>Unified timeline</h2>
        <p className="tiny muted">
          Recorded milestones from the latest 10 releases. Database status above
          reflects the current state.
        </p>
        {!data.events.length && (
          <p className="muted">Your first deployment will appear here.</p>
        )}
        <ol className="advanced-timeline">
          {[...data.events].reverse().map((event, i) => (
            <li key={`${event.deployment_id}-${i}`}>
              <time dateTime={event.at}>
                {new Date(event.at).toLocaleString()}
              </time>
              <strong>{event.stage}</strong>
              <span className="mono tiny">
                {event.deployment_id.slice(0, 8)}
              </span>
              {event.error && <p className="platform-failure">{event.error}</p>}
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function Previews({ projectId, role }: { projectId: string; role: string }) {
  const { data, error, reload } = useApi(
    () => workspace.previews(projectId),
    [projectId],
    15_000,
  );
  return (
    <div className="stack">
      <h2>Review changes before release</h2>
      <p className="muted">
        Enable previews for a component, then open or update a pull request from
        the same repository. Configure preview variables in Environment.
        Production secrets and database links are not inherited. Previews use
        additional cluster capacity.
      </p>
      {error && <p className="alert error">{error}</p>}
      {!data && !error && <CardSkeleton />}
      {data?.length === 0 && (
        <p className="platform-notice">
          Add a web, static, or function component to use previews.
        </p>
      )}
      {data?.map((group) => (
        <PreviewCard
          key={group.service_id}
          group={group}
          projectId={projectId}
          role={role}
          reload={reload}
        />
      ))}
    </div>
  );
}

function PreviewCard({
  group,
  projectId,
  role,
  reload,
}: {
  group: PreviewGroup;
  projectId: string;
  role: string;
  reload: () => void;
}) {
  const [edit, setEdit] = useState<PreviewPolicy | null>(null);
  const value = edit ?? group.policy;
  const { run, feedback, busy } = useAction(reload);
  const active = group.previews.filter((p) => !p.removed_at);
  return (
    <article className="card stack">
      <div className="row">
        <h3>{group.name}</h3>
        <span className="grow" />
        <span className="platform-role">
          {group.policy.enabled ? "Enabled" : "Disabled"}
        </span>
      </div>
      {feedback}
      {role === "owner" && (
        <form
          className="advanced-form"
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              await workspace.savePreview(projectId, group.service_id, value);
              setEdit(null);
            }, "Preview policy saved. Disabled previews are cleaned up on the next sweep.");
          }}
        >
          <label className="row">
            <input
              type="checkbox"
              checked={value.enabled}
              onChange={(e) => setEdit({ ...value, enabled: e.target.checked })}
            />
            Enable PR previews
          </label>
          <label>
            Lifetime (hours)
            <input
              type="number"
              min={1}
              max={168}
              value={value.ttl_hours}
              onChange={(e) =>
                setEdit({ ...value, ttl_hours: Number(e.target.value) })
              }
            />
          </label>
          <label>
            Maximum previews
            <input
              type="number"
              min={1}
              max={3}
              value={value.max_active}
              onChange={(e) =>
                setEdit({ ...value, max_active: Number(e.target.value) })
              }
            />
          </label>
          <Button loading={busy} type="submit">
            Save policy
          </Button>
        </form>
      )}
      {!active.length && (
        <p className="muted">
          No active previews. Once enabled, the next PR update starts a build.
        </p>
      )}
      {active.map((p) => (
        <div className="workspace-dependency" key={p.pr}>
          <div>
            <strong>PR #{p.pr}</strong>{" "}
            <span className="mono tiny">{shortSha(p.commit_sha)}</span>
            <p className="tiny muted">
              Expires {new Date(p.expires_at).toLocaleString()}
            </p>
            {p.status === "live" && (
              <a href={p.url} target="_blank" rel="noreferrer">
                Open preview ↗
              </a>
            )}
          </div>
          <StatusBadge status={p.status} />
          {role !== "viewer" && (
            <Button
              disabled={busy}
              onClick={() =>
                void run(
                  () =>
                    workspace.removePreview(projectId, group.service_id, p.pr),
                  "Preview removed",
                )
              }
            >
              Remove preview
            </Button>
          )}
        </div>
      ))}
    </article>
  );
}

function Variables({
  projectId,
  services,
}: {
  projectId: string;
  services: { id: string; name: string }[];
}) {
  const { data, error, reload } = useApi(
    () => workspace.variables(projectId),
    [projectId],
  );
  const [service, setService] = useState(services[0]?.id ?? "");
  const [environment, setEnvironment] = useState<"production" | "preview">(
    "preview",
  );
  const [key, setKey] = useState(""),
    [value, setValue] = useState("");
  const [secret, setSecret] = useState(true),
    [build, setBuild] = useState(false);
  const { busy, run, feedback } = useAction(reload);
  return (
    <div className="stack">
      <h2>Scoped environment variables</h2>
      <p className="muted">
        Values are encrypted and write-only. Changes apply on the next
        deployment. Production values override service variables; linked
        database variables and platform variables take precedence.
      </p>
      {error && <p className="alert error">{error}</p>}
      {feedback}
      <form
        className="card advanced-form"
        onSubmit={(e) => {
          e.preventDefault();
          void run(async () => {
            await workspace.saveVariable(projectId, {
              service_id: service,
              environment,
              key,
              value,
              is_secret: secret,
              build_time: build,
            });
            setValue("");
            setKey("");
          }, "Variable saved for the next deployment");
        }}
      >
        <label>
          Component
          <select value={service} onChange={(e) => setService(e.target.value)}>
            {services.map((s) => (
              <option value={s.id} key={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Environment
          <select
            value={environment}
            onChange={(e) =>
              setEnvironment(e.target.value as "production" | "preview")
            }
          >
            <option value="preview">Preview</option>
            <option value="production">Production</option>
          </select>
        </label>
        <label>
          Variable name
          <input
            required
            pattern="[A-Za-z_][A-Za-z0-9_]*"
            maxLength={255}
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="API_BASE_URL"
          />
        </label>
        <label>
          Value
          <input
            required
            type={secret ? "password" : "text"}
            autoComplete="off"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </label>
        <label className="row">
          <input
            type="checkbox"
            checked={secret}
            onChange={(e) => {
              setSecret(e.target.checked);
              if (e.target.checked) setBuild(false);
            }}
          />
          Secret
        </label>
        <label className="row">
          <input
            type="checkbox"
            disabled={secret}
            checked={build}
            onChange={(e) => setBuild(e.target.checked)}
          />
          Available during build
        </label>
        <Button type="submit" loading={busy} disabled={!service}>
          Save variable
        </Button>
      </form>
      <div className="advanced-table">
        <table>
          <thead>
            <tr>
              <th>Component</th>
              <th>Environment</th>
              <th>Name</th>
              <th>Visibility</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {data?.map((v) => (
              <tr key={v.id}>
                <td>{services.find((s) => s.id === v.service_id)?.name}</td>
                <td>{v.environment}</td>
                <td>
                  <code>{v.key}</code>
                </td>
                <td>
                  {v.is_secret
                    ? "Secret"
                    : v.build_time
                      ? "Build + runtime"
                      : "Runtime"}
                </td>
                <td>
                  <Button
                    disabled={busy}
                    onClick={() =>
                      void run(
                        () => workspace.deleteVariable(projectId, v.id),
                        "Variable removed for future deployments",
                      )
                    }
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data?.length === 0 && <p className="muted">No scoped variables yet.</p>}
    </div>
  );
}

function Team({ projectId, owner }: { projectId: string; owner: boolean }) {
  const { data, error, reload } = useApi(
    () => workspace.members(projectId),
    [projectId],
    30_000,
  );
  const [email, setEmail] = useState(""),
    [role, setRole] = useState("viewer"),
    [inviteLink, setInviteLink] = useState("");
  const { run, busy, feedback } = useAction(reload);
  return (
    <div className="stack">
      <h2>Team access</h2>
      <p className="muted">
        Viewers can inspect this platform. Developers can deploy, manage scoped
        variables, and remove previews. The owner manages membership, preview
        policies, billing, and databases.
      </p>
      {error && <p className="alert error">{error}</p>}
      {feedback}
      {owner && (
        <form
          className="card advanced-form"
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              const result = await workspace.invite(projectId, email, role);
              setInviteLink(
                `${window.location.origin}/workspace-invitation#${result.token}`,
              );
              setEmail("");
            }, "Invitation created. Share the link with the intended teammate; it expires in 7 days.");
          }}
        >
          <label>
            Teammate email
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label>
            Role
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="viewer">Viewer</option>
              <option value="developer">Developer</option>
            </select>
          </label>
          <Button type="submit" loading={busy}>
            Create invitation link
          </Button>
        </form>
      )}
      {inviteLink && (
        <div className="card">
          <label>
            Invitation link
            <input
              readOnly
              value={inviteLink}
              onFocus={(e) => e.target.select()}
            />
          </label>
          <p className="tiny muted">
            Only the verified account matching the invited email can accept.
          </p>
        </div>
      )}
      <div className="advanced-table">
        <table>
          <thead>
            <tr>
              <th>Member</th>
              <th>Role</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {data?.map((member) => (
              <tr key={member.user_id}>
                <td>{member.email}</td>
                <td>
                  {owner && member.role !== "owner" ? (
                    <select
                      aria-label={`Role for ${member.email}`}
                      disabled={busy}
                      value={member.role}
                      onChange={(e) =>
                        void run(
                          () =>
                            workspace.role(
                              projectId,
                              member.user_id,
                              e.target.value,
                            ),
                          "Role updated",
                        )
                      }
                    >
                      <option value="viewer">Viewer</option>
                      <option value="developer">Developer</option>
                    </select>
                  ) : (
                    member.role
                  )}
                </td>
                <td>
                  {owner && member.role !== "owner" && (
                    <Button
                      disabled={busy}
                      onClick={() =>
                        void run(
                          () => workspace.remove(projectId, member.user_id),
                          "Member removed",
                        )
                      }
                    >
                      Remove access
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {owner && <Invitations projectId={projectId} key={inviteLink} />}
    </div>
  );
}

function Invitations({ projectId }: { projectId: string }) {
  const { data, error, reload } = useApi(
    () => workspace.invitations(projectId),
    [projectId],
  );
  const { busy, run, feedback } = useAction(reload);
  return (
    <section className="card">
      <h3>Pending invitations</h3>
      {error && <p className="alert error">{error}</p>}
      {feedback}
      {data?.length === 0 && <p className="muted">No pending invitations.</p>}
      {data?.map((i) => (
        <div className="workspace-dependency" key={i.id}>
          <span>
            {i.email} · {i.role}
          </span>
          <Button
            disabled={busy}
            onClick={() =>
              void run(
                () => workspace.revoke(projectId, i.id),
                "Invitation revoked",
              )
            }
          >
            Revoke
          </Button>
        </div>
      ))}
    </section>
  );
}

function Activity({ projectId }: { projectId: string }) {
  const { data, error } = useApi(
    () => workspace.activity(projectId),
    [projectId],
    15_000,
  );
  return (
    <section className="card">
      <h2>Workspace activity</h2>
      <p className="tiny muted">
        Latest 100 project actions. Secret values are never included.
      </p>
      {error && <p className="alert error">{error}</p>}
      {data?.length === 0 && <p className="muted">No project activity yet.</p>}
      <ol className="advanced-timeline">
        {data?.map((e) => (
          <li key={e.id}>
            <time>{new Date(e.at).toLocaleString()}</time>
            <strong>
              {e.action.replaceAll(".", " ").replaceAll("_", " ")}
            </strong>
            <span className="tiny muted">{e.actor}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function WorkspaceInvitation() {
  const [token, setToken] = useState(() => window.location.hash.slice(1));
  const navigate = useNavigate();
  const { busy, run, feedback } = useAction(() => {});
  return (
    <section className="card stack">
      <p className="platform-eyebrow">TEAM INVITATION</p>
      <h1>Join a platform</h1>
      <p>
        Sign in using the invited email and verify it before accepting. You can
        paste an invitation token below.
      </p>
      {feedback}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void run(async () => {
            const result = await workspace.accept(token);
            window.history.replaceState(null, "", window.location.pathname);
            navigate(`/projects/${result.project_id}/workspace`);
          }, "Invitation accepted");
        }}
      >
        <label>
          Invitation token
          <input
            type="password"
            required
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
        </label>
        <Button loading={busy} type="submit">
          Accept invitation
        </Button>
      </form>
    </section>
  );
}

